import type { ProductDocument } from 'models/product';
import { useQuery } from '@tanstack/react-query';
import { getBidsCountByProductId } from '@util/firestore/bid/bid.service';
import { cn } from '@util/index';
import { getSizeLabel } from '@util/createListingHelpers';

export const ProductTitle = ({ product }: { product: ProductDocument }) => {
  const { data: bidCount } = useQuery({
    queryKey: ['bidCount', product.id],
    queryFn: () => getBidsCountByProductId(product.id),
    enabled: !!product.is_auction,
  });

  const hasSizes =
    product.size?.letter ||
    product.size?.number ||
    !!product.variations?.length;

  return (
    <div className="flex flex-col gap-4">
      <span
        className={cn(
          'line-clamp-2 min-h-[5.3rem] overflow-hidden text-ellipsis text-[1.8rem] font-medium leading-relaxed'
        )}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {product.title}
      </span>

      {product.is_auction && (
        <div className="flex w-full items-center">
          <div className="flex w-full items-center gap-4">
            <span className="text-[1.6rem] text-zinc-500 sm:text-[1.8rem]">
              Auction
            </span>

            <div className="h-2 w-2 rounded-full bg-zinc-400" />

            <span className="whitespace-nowrap text-[1.6rem] text-gray-700 sm:text-[1.8rem]">
              {bidCount !== undefined ? `${bidCount} Bids` : ''}
            </span>
          </div>
        </div>
      )}

      {!product.is_auction && (
        <div className="flex w-full items-center">
          <div className="flex w-full items-center gap-4">
            <span className="text-[1.6rem] text-zinc-500 sm:text-[1.8rem]">
              {product.condition}
            </span>

            {hasSizes && <div className="h-2 w-2 rounded-full bg-zinc-400" />}

            {hasSizes ? (
              <span className="whitespace-nowrap text-[1.6rem] text-gray-500 sm:text-[1.8rem]">
                {product.size?.letter && !product.has_variations
                  ? getSizeLabel(product.size.letter)
                  : ''}{' '}
                {product.size?.number && !product.has_variations
                  ? `${product.size?.letter ? '/ ' : ''} Size ${
                      product.size?.number
                    }`
                  : ''}
                {!!product.variations?.length && product.variations.length > 1
                  ? 'Multiple sizes'
                  : ''}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
